import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { CLOSE_ICON, TOAST_ICONS } from './icons';
import { ToastService } from './toast.service';
import { Toast, ToastAction, ToastType } from './types';

type ToastColors = Record<'toast' | 'description' | 'icon', string>;

const DEFAULT_TOAST = 'bg-white border-gray-200 text-gray-900';
const DEFAULT_DESCRIPTION = 'text-gray-600';

const ICON_COLORS: Record<ToastType, string> = {
  default: 'text-gray-500',
  loading: 'text-gray-500',
  success: 'text-success-500',
  info: 'text-primary-500',
  warning: 'text-warning-500',
  error: 'text-danger-500',
};

/** Same palette as the alert component. */
const RICH_COLORS: Partial<Record<ToastType, Omit<ToastColors, 'icon'>>> = {
  success: {
    toast: 'bg-success-100 border-success-200 text-success-800',
    description: 'text-success-700',
  },
  info: {
    toast: 'bg-primary-100 border-primary-200 text-primary-800',
    description: 'text-primary-700',
  },
  warning: {
    toast: 'bg-warning-100 border-warning-200 text-warning-800',
    description: 'text-warning-700',
  },
  error: {
    toast: 'bg-danger-100 border-danger-200 text-danger-800',
    description: 'text-danger-700',
  },
};

export interface ToastDefaults {
  duration: number;
  richColors: boolean;
  closeButton: boolean;
  closeButtonAriaLabel: string;
}

@Component({
  selector: 'li[wma-toast]',
  templateUrl: './toast.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'data-wma-toast': '',
    // Toasts hidden beyond `visibleToasts` must not be reachable with the keyboard or a screen reader.
    '[attr.tabindex]': 'visible() ? 0 : -1',
    '[attr.inert]': 'visible() ? null : ""',
    '[attr.data-type]': 'toast().type',
    '[attr.data-mounted]': 'mounted()',
    '[attr.data-removed]': 'toast().removed',
    '[attr.data-dismissible]': 'toast().dismissible !== false',
    '[attr.data-visible]': 'visible()',
    '[attr.data-front]': 'index() === 0',
    '[attr.data-expanded]': 'expanded()',
    '[attr.data-index]': 'index()',
    '[style.--toasts-before]': 'index()',
    '[style.--z-index]': 'count() - index()',
    '[style.--offset]': 'displayedOffset() + "px"',
    '[style.--initial-height]': 'initialHeight() + "px"',
    '[class]': 'classes()',
  },
})
export class ToastComponent {
  readonly #service = inject(ToastService);
  readonly #host = inject<ElementRef<HTMLElement>>(ElementRef);
  readonly #destroyRef = inject(DestroyRef);
  private readonly content = viewChild.required<ElementRef<HTMLElement>>('content');

  readonly toast = input.required<Toast>();
  /** Position in the stack, 0 being the front (newest) toast. */
  readonly index = input.required<number>();
  /** Number of toasts in the stack. */
  readonly count = input.required<number>();
  /** Distance in px from the front toast when the stack is expanded. */
  readonly offset = input.required<number>();
  readonly visible = input.required<boolean>();
  readonly expanded = input.required<boolean>();
  /** Pauses the auto-close timer (hover, focus, hidden page…). */
  readonly paused = input.required<boolean>();
  readonly defaults = input.required<ToastDefaults>();

  /** Natural height of the toast, used to stack the following ones. */
  readonly heightChange = output<number>();

  protected readonly mounted = signal(false);
  protected readonly initialHeight = signal(0);

  protected readonly closeIcon = CLOSE_ICON;
  protected readonly icon = computed(() => TOAST_ICONS[this.toast().type]);
  protected readonly showCloseButton = computed(
    () =>
      (this.toast().closeButton ?? this.defaults().closeButton) &&
      this.toast().dismissible !== false &&
      this.toast().type !== 'loading',
  );
  /** A removed toast keeps the offset it had so its exit animation starts from where it was. */
  protected readonly displayedOffset = linkedSignal<{ offset: number; removed: boolean }, number>({
    source: () => ({ offset: this.offset(), removed: this.toast().removed }),
    computation: ({ offset, removed }, previous) => (removed && previous ? previous.value : offset),
  });
  protected readonly colors = computed<ToastColors>(() => {
    const { type, richColors } = this.toast();
    const rich = (richColors ?? this.defaults().richColors) ? RICH_COLORS[type] : undefined;
    return {
      toast: rich?.toast ?? DEFAULT_TOAST,
      description: rich?.description ?? DEFAULT_DESCRIPTION,
      icon: ICON_COLORS[type],
    };
  });
  protected readonly classes = computed(() =>
    [
      'flex items-start gap-3 rounded-lg border p-4 text-sm shadow-lg',
      'focus-visible:ring-2 focus-visible:ring-primary-500',
      this.colors().toast,
      this.toast().class ?? '',
    ].join(' '),
  );

  #timerVersion = -1;
  #remaining = 0;

  constructor() {
    this.#autoCloseOnTimeout();

    afterNextRender(() => {
      this.#measure();
      // The measure forced a style computation, so the entry transition can start now.
      this.mounted.set(true);

      if (typeof ResizeObserver !== 'undefined') {
        const observer = new ResizeObserver(() => this.#measure());
        observer.observe(this.content().nativeElement);
        this.#destroyRef.onDestroy(() => observer.disconnect());
      }
    });
  }

  protected dismiss(): void {
    this.#service.dismiss(this.toast().id);
  }

  protected onAction(event: MouseEvent, action: ToastAction): void {
    action.onClick(event);
    if (!event.defaultPrevented) {
      this.#service.close(this.toast().id);
    }
  }

  protected onCancel(event: MouseEvent, cancel: ToastAction): void {
    if (this.toast().dismissible === false) {
      return;
    }
    cancel.onClick(event);
    this.#service.close(this.toast().id);
  }

  #autoCloseOnTimeout(): void {
    effect((onCleanup) => {
      const { id, type, version, removed, duration: toastDuration } = this.toast();
      const duration = toastDuration ?? this.defaults().duration;

      // A new or updated toast gets its full duration again.
      if (version !== this.#timerVersion) {
        this.#timerVersion = version;
        this.#remaining = duration;
      }

      if (removed || type === 'loading' || this.paused() || !Number.isFinite(duration)) {
        return;
      }

      const startedAt = Date.now();
      const timeout = setTimeout(() => this.#service.autoClose(id), this.#remaining);
      onCleanup(() => {
        clearTimeout(timeout);
        this.#remaining -= Date.now() - startedAt;
      });
    });
  }

  /** Measures the natural height, ignoring the height forced by the collapsed stack. */
  #measure(): void {
    if (this.toast().removed) {
      return;
    }
    const element = this.#host.nativeElement;
    const height = element.style.height;
    element.style.height = 'auto';
    // `offsetHeight` ignores the scale applied to the toasts behind the front one.
    const naturalHeight = element.offsetHeight;
    element.style.height = height;

    if (naturalHeight !== this.initialHeight()) {
      this.initialHeight.set(naturalHeight);
      this.heightChange.emit(naturalHeight);
    }
  }
}
